import React, { useState } from 'react'
import api from '../../../utils/axiosInstance'
import Select, { GroupBase } from 'react-select'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts'
import Flatpickr from 'react-flatpickr'
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import 'flatpickr/dist/themes/dark.css'
import { Categoria } from './hooks/useVarList'
import { USE_MOCK } from '../../../utils/mock/mockFlag'
import { mockCorrelacao } from '../../../utils/mock/mockData'
import { getApiBaseUrl } from '../../../utils/defines/apiUrl'

/* Flatpickr overrides — mesmo padrão da Sidebar */
const FP_STYLE = `
.cor-fp-wrap .flatpickr-input {
    width: 100%; box-sizing: border-box;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 10px; color: #fff;
    font-family: 'Nunito', sans-serif; font-size: 12px;
    padding: 8px 11px; outline: none; cursor: pointer;
    transition: border-color 0.15s;
}
.cor-fp-wrap .flatpickr-input:focus,
.cor-fp-wrap .flatpickr-input.active { border-color: #5292D1; }
.flatpickr-calendar { font-family: 'Nunito', sans-serif; }
@media (max-width: 600px) {
    .cor-fp-wrap .flatpickr-input { font-size: 16px; }
}
`

interface CorPar { campo1: string; campo2: string; r: number }
interface EvPar  { campo: string; r: number }
interface Result { pares: (CorPar | EvPar)[]; modo: 'matriz' | 'evento' }

interface Props {
    token: string
    categorias: Categoria[]
    /** Ref preenchida com função que abre o modal de ajuda — chamada pelo header externo */
    infoRef?: React.MutableRefObject<(() => void) | null>
}

// ── Helpers ────────────────────────────────────────────────────────

function rColor(r: number) {
    const a = Math.abs(r)
    if (a >= 0.7) return r > 0 ? '#92B258' : '#F03F3F'
    if (a >= 0.3) return '#E5D34A'
    return 'rgba(255,255,255,0.3)'
}

function rText(r: number, label1?: string, label2?: string): string {
    const a = Math.abs(r)
    const dir = r > 0 ? 'positiva' : 'inversa'
    const var1 = label1 ? `"${label1}"` : 'as variáveis'
    const var2 = label2 ? `"${label2}"` : ''
    if (a >= 0.7) return `Relação forte ${dir}. Quando ${var1} ${r > 0 ? 'sobe' : 'sobe'}, ${var2 || 'a outra'} tende a ${r > 0 ? 'subir' : 'cair'} junto. Vale investigar.`
    if (a >= 0.3) return `Relação moderada ${dir} — pode ser relevante, mas não conclusiva.`
    return `Relação fraca (r=${r.toFixed(2)}). Provavelmente coincidência.`
}

function rEvText(r: number, label?: string): string {
    const a = Math.abs(r)
    const v = label ? `"${label}"` : 'esta variável'
    if (a >= 0.7) return `${v} tem relação forte com o evento. Costuma estar ${r > 0 ? 'alta' : 'baixa'} quando o evento ocorre.`
    if (a >= 0.3) return `${v} tem relação moderada. Pode ser um indicador, mas não é determinante sozinho.`
    return `${v} não mostrou relação clara com o evento.`
}

function labelFor(campo: string, categorias: Categoria[]): string {
    for (const cat of categorias) {
        const v = cat.variaveis.find(v => v.campo === campo)
        if (v) return v.label
    }
    return campo.split('_').slice(-2).join(' ')
}

// ── Mini modal overlay ─────────────────────────────────────────────

const Overlay: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: '#1a2330', borderRadius: 20, padding: '24px 28px', maxWidth: 520, width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 15, color: '#fff' }}>{title}</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
            {children}
        </div>
    </div>
)

// ── Texto de ajuda ─────────────────────────────────────────────────

const INFO_TEXT = () => (
    <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
        <p style={{ marginTop: 0 }}>
            A correlação mostra se duas variáveis <strong style={{ color: '#fff' }}>caminham juntas</strong> ao longo do tempo.
        </p>
        <p>
            <strong style={{ color: '#fff' }}>Exemplo prático:</strong> se toda vez que a temperatura do óleo sobe, a umidade também sobe, dizemos que elas têm correlação positiva alta.
        </p>
        <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>O número <strong>r</strong> vai de −1 a +1:</div>
            <div>• <span style={{ color: '#92B258', fontWeight: 700 }}>r próximo de +1</span> → as duas sobem juntas</div>
            <div>• <span style={{ color: '#F03F3F', fontWeight: 700 }}>r próximo de −1</span> → quando uma sobe, a outra cai</div>
            <div>• <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 700 }}>r próximo de 0</span> → sem relação clara</div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
            <div style={{ marginBottom: 4, fontWeight: 700, color: '#fff' }}>Guia de referência:</div>
            <div>• <span style={{ color: '#92B258' }}>|r| &gt; 0,7</span> → Relação forte: vale investigar</div>
            <div>• <span style={{ color: '#E5D34A' }}>0,3 a 0,7</span> → Relação moderada: pode ser relevante</div>
            <div>• <span style={{ color: 'rgba(255,255,255,0.4)' }}>&lt; 0,3</span> → Relação fraca: provavelmente coincidência</div>
        </div>
        <p style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
            Atenção: correlação não prova que uma variável causa a outra. Use como ponto de partida para investigação.
        </p>
        <div style={{ background: 'rgba(82,146,209,0.1)', borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ color: '#5292D1', fontWeight: 700, marginBottom: 4 }}>Modo Evento</div>
            <div>Defina um limite de alerta — por exemplo, <strong>Temperatura do Óleo &gt; 60°C</strong>. O sistema identifica quais outras variáveis costumam estar altas (ou baixas) exatamente nos momentos em que esse evento ocorre.</div>
        </div>
    </div>
)

// ── Resultado ──────────────────────────────────────────────────────

const ResultContent: React.FC<{ result: Result; categorias: Categoria[] }> = ({ result, categorias }) => {
    if (result.modo === 'evento') {
        const pares = result.pares as EvPar[]
        return (
            <div>
                <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 16 }}>
                    Variáveis ordenadas pela força da relação com o evento definido.
                </div>
                <ResponsiveContainer width="100%" height={Math.max(120, pares.length * 36)}>
                    <BarChart data={pares.map(p => ({ name: labelFor(p.campo, categorias), r: p.r }))} layout="vertical" margin={{ left: 4, right: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                        <XAxis type="number" domain={[-1, 1]} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} tickLine={false} />
                        <YAxis type="category" dataKey="name" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} width={120} tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={{ background: '#202833', border: 'none', borderRadius: 8, fontSize: 12 }} formatter={(v: any) => [Number(v).toFixed(3), 'r']} />
                        <Bar dataKey="r" radius={[0, 4, 4, 0]} label={{ position: 'right', fill: 'rgba(255,255,255,0.6)', fontSize: 11, formatter: (v: any) => Number(v).toFixed(2) }}>
                            {pares.map((p, i) => <Cell key={i} fill={rColor(p.r)} />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 16 }}>
                    {pares.map((p, i) => (
                        <div key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '8px 0', fontFamily: 'Nunito,sans-serif', fontSize: 12 }}>
                            <span style={{ color: rColor(p.r), fontWeight: 700 }}>{labelFor(p.campo, categorias)}</span>
                            <span style={{ color: 'rgba(255,255,255,0.5)', marginLeft: 6 }}>r = {p.r.toFixed(3)}</span>
                            <div style={{ color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>{rEvText(p.r, labelFor(p.campo, categorias))}</div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    const pares = result.pares as CorPar[]
    return (
        <div>
            <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 16 }}>
                Cada linha mostra a relação entre um par de variáveis.
            </div>
            {pares.map((p, i) => {
                const l1 = labelFor(p.campo1, categorias)
                const l2 = labelFor(p.campo2, categorias)
                const a = Math.abs(p.r)
                const strength = a >= 0.7 ? 'Forte' : a >= 0.3 ? 'Moderada' : 'Fraca'
                return (
                    <div key={i} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, color: '#fff' }}>{l1} <span style={{ color: 'rgba(255,255,255,0.35)' }}>×</span> {l2}</span>
                            <span style={{ background: rColor(p.r), color: '#fff', borderRadius: 6, padding: '2px 8px', fontWeight: 700, fontSize: 12, minWidth: 48, textAlign: 'center' }}>{p.r.toFixed(2)}</span>
                        </div>
                        <div style={{ fontSize: 11, color: rColor(p.r), fontWeight: 700, marginBottom: 2 }}>{strength}</div>
                        <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>{rText(p.r, l1, l2)}</div>
                    </div>
                )
            })}
        </div>
    )
}

// ── Seletor de variáveis — accordion com chips em 2 colunas ────────

const VarPicker: React.FC<{ categorias: Categoria[]; selected: string[]; onToggle: (c: string) => void }> = ({ categorias, selected, onToggle }) => {
    const [openCats, setOpenCats] = useState<Set<number>>(() => new Set([0]))

    const toggle = (i: number) => setOpenCats(prev => {
        const next = new Set(prev)
        next.has(i) ? next.delete(i) : next.add(i)
        return next
    })

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {categorias.map((cat, i) => {
                const catSelected = cat.variaveis.filter(v => selected.includes(v.campo)).length
                const isOpen = openCats.has(i)
                return (
                    <div key={i} style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)' }}>
                        {/* Cabeçalho da categoria */}
                        <button onClick={() => toggle(i)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: isOpen ? 'rgba(82,146,209,0.1)' : 'rgba(255,255,255,0.03)', border: 'none', cursor: 'pointer', color: '#fff', fontFamily: 'Montserrat,sans-serif', fontSize: 12, fontWeight: 700 }}>
                            <span>{cat.nome}</span>
                            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                {catSelected > 0 && <span style={{ background: '#5292D1', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{catSelected}</span>}
                                <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>{isOpen ? '▲' : '▼'}</span>
                            </span>
                        </button>
                        {/* Chips em grid 2 colunas */}
                        {isOpen && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: '10px 14px 14px', background: 'rgba(0,0,0,0.1)' }}>
                                {cat.variaveis.map(v => {
                                    const on = selected.includes(v.campo)
                                    return (
                                        <button
                                            key={v.campo}
                                            onClick={() => onToggle(v.campo)}
                                            style={{
                                                padding: '7px 10px', borderRadius: 10, textAlign: 'left',
                                                border: `1px solid ${on ? 'rgba(82,146,209,0.5)' : 'rgba(255,255,255,0.1)'}`,
                                                background: on ? 'rgba(82,146,209,0.2)' : 'rgba(255,255,255,0.03)',
                                                color: on ? '#7BB8E8' : 'rgba(255,255,255,0.6)',
                                                fontSize: 12, fontFamily: 'Nunito,sans-serif', cursor: 'pointer',
                                                fontWeight: on ? 700 : 400, lineHeight: 1.3,
                                                transition: 'all 0.12s',
                                            }}
                                        >{v.label}</button>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

// ── Main component ─────────────────────────────────────────────────

const CorrelationPanel: React.FC<Props> = ({ token, categorias, infoRef }) => {
    // Período próprio da análise de correlação — padrão: últimas 24 horas
    const now = new Date()
    const [corInicio, setCorInicio] = useState(() => new Date(now.getTime() - 24 * 60 * 60 * 1000))
    const [corFim,    setCorFim]    = useState(() => new Date())

    // Refs para atualizar o Flatpickr diretamente (sem passar por value controlado)
    // — evita o flash da string UTC durante re-renders
    const fpInicioRef = React.useRef<any>(null)
    const fpFimRef    = React.useRef<any>(null)

    /** Aplica um atalho de período sem flash: atualiza estado + instância Flatpickr diretamente */
    const applyShortcut = (ms: number) => {
        const n = new Date()
        const newInicio = new Date(n.getTime() - ms)
        setCorInicio(newInicio)
        setCorFim(n)
        fpInicioRef.current?.flatpickr?.setDate(newInicio, false)
        fpFimRef.current?.flatpickr?.setDate(n, false)
    }

    const [selectedCampos, setSelectedCampos] = useState<string[]>([])
    const [eventoAtivo, setEventoAtivo] = useState(false)
    const [eventoCampo, setEventoCampo] = useState('')
    const [eventoCondicao, setEventoCondicao] = useState<'>' | '<' | '='>('>')
    const [eventoValor, setEventoValor] = useState('')
    const [result, setResult] = useState<Result | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [showInfo, setShowInfo] = useState(false)
    const [showResult, setShowResult] = useState(false)

    // Expõe o trigger para o header externo
    React.useEffect(() => {
        if (infoRef) infoRef.current = () => setShowInfo(true)
    })

    const allVars = categorias.flatMap(c => c.variaveis)

    const toggleCampo = (campo: string) => {
        setSelectedCampos(prev => prev.includes(campo) ? prev.filter(c => c !== campo) : [...prev, campo])
    }

    const run = async () => {
        if (selectedCampos.length < 2) { setError('Selecione ao menos 2 variáveis'); return }
        setError(null); setLoading(true)
        try {
            if (USE_MOCK) {
                await new Promise(r => setTimeout(r, 500))
                setResult(eventoAtivo
                    ? { pares: mockCorrelacao.evento.map(e => ({ campo: e.campo, r: e.r })), modo: 'evento' }
                    : { pares: mockCorrelacao.pares.map(p => ({ campo1: p.campo1, campo2: p.campo2, r: p.r })), modo: 'matriz' })
                setShowResult(true)
                setLoading(false)
                return
            }
            const body: any = { token, campos: selectedCampos, inicio: corInicio.toISOString(), fim: corFim.toISOString() }
            if (eventoAtivo && eventoCampo && eventoValor) {
                body.evento = { campo: eventoCampo, condicao: eventoCondicao, valor: Number(eventoValor) }
            }
            const r = await api.post(`${getApiBaseUrl()}/graficos/correlacao`, body)
            setResult(r.data)
            setShowResult(true)
        } catch (e: any) {
            setError(e.message)
        } finally { setLoading(false) }
    }

    const CARD: React.CSSProperties = { background: 'rgba(94,94,94,0.1)', backdropFilter: 'blur(4px)', borderRadius: 20, padding: '14px 16px', marginBottom: 10, boxSizing: 'border-box' }
    const FILTER_BTN: React.CSSProperties = { width: '100%', padding: '8px 0', borderRadius: 10, background: 'linear-gradient(90deg,#5292D1,#92B258)', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 13 }
    const INPUT_STYLE: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', fontSize: 16, boxSizing: 'border-box' as const, outline: 'none' }
    const COND_STYLE: React.CSSProperties = { ...INPUT_STYLE, width: 52 }

    // react-select options agrupados por categoria
    type Opt = { value: string; label: string }
    const eventoOptions: GroupBase<Opt>[] = categorias.map(cat => ({
        label: cat.nome,
        options: cat.variaveis.map(v => ({ value: v.campo, label: v.label }))
    }))
    const eventoSelected = eventoOptions.flatMap(g => (g as any).options as Opt[]).find(o => o.value === eventoCampo) ?? null
    const SELECT_FONT = 'Nunito, sans-serif'
    const selectStyles = {
        control: (b: any, s: any) => ({ ...b, background: 'rgba(255,255,255,0.07)', borderColor: s.isFocused ? '#5292D1' : 'rgba(255,255,255,0.15)', borderRadius: 8, fontSize: 12, fontFamily: SELECT_FONT, boxShadow: 'none', minHeight: 34, cursor: 'pointer', '&:hover': { borderColor: '#5292D1' } }),
        menu: (b: any) => ({ ...b, background: '#1e2b3a', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', zIndex: 9999 }),
        menuPortal: (b: any) => ({ ...b, zIndex: 9999 }),
        option: (b: any, s: any) => ({ ...b, background: s.isSelected ? '#5292D1' : s.isFocused ? 'rgba(82,146,209,0.2)' : 'transparent', color: s.isSelected ? '#fff' : 'rgba(255,255,255,0.8)', fontSize: 12, fontFamily: SELECT_FONT, cursor: 'pointer' }),
        groupHeading: (b: any) => ({ ...b, color: '#5292D1', fontSize: 10, fontFamily: SELECT_FONT, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const }),
        singleValue: (b: any) => ({ ...b, color: '#fff', fontSize: 12, fontFamily: SELECT_FONT }),
        placeholder: (b: any) => ({ ...b, color: 'rgba(255,255,255,0.3)', fontSize: 12, fontFamily: SELECT_FONT }),
        input: (b: any) => ({ ...b, color: '#fff', fontSize: 16, fontFamily: SELECT_FONT }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (b: any) => ({ ...b, color: 'rgba(255,255,255,0.3)', padding: '0 6px' }),
        clearIndicator: (b: any) => ({ ...b, color: 'rgba(255,255,255,0.3)' }),
    }

    const SECTION_LABEL: React.CSSProperties = { fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }
    const DIVIDER: React.CSSProperties = { height: 1, background: 'rgba(255,255,255,0.06)', margin: '20px 0' }

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

                {/* ── 1. Variáveis ── */}
                <span style={SECTION_LABEL}>1 — Selecione as variáveis</span>

                {/* Chips selecionadas */}
                {selectedCampos.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
                        {selectedCampos.map(c => (
                            <span key={c} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, background: 'rgba(82,146,209,0.15)', border: '1px solid rgba(82,146,209,0.3)', fontSize: 12, color: '#7BB8E8', fontFamily: 'Nunito,sans-serif' }}>
                                {labelFor(c, categorias)}
                                <button onClick={() => toggleCampo(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(123,184,232,0.6)', fontSize: 13, lineHeight: 1, padding: 0 }}>✕</button>
                            </span>
                        ))}
                    </div>
                )}

                {categorias.length > 0
                    ? <VarPicker categorias={categorias} selected={selectedCampos} onToggle={toggleCampo} />
                    : <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>Carregando variáveis...</div>
                }

                <div style={DIVIDER} />

                {/* ── 2. Evento (opcional) ── */}
                <span style={SECTION_LABEL}>2 — Evento (opcional)</span>
                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: '12px 14px', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontFamily: 'Nunito,sans-serif', color: eventoAtivo ? '#fff' : 'rgba(255,255,255,0.55)', cursor: 'pointer', marginBottom: eventoAtivo ? 12 : 0 }}>
                        <input type="checkbox" checked={eventoAtivo} onChange={e => setEventoAtivo(e.target.checked)} style={{ accentColor: '#5292D1', width: 15, height: 15 }} />
                        Analisar em relação a um evento
                    </label>

                    {eventoAtivo && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: 'Nunito,sans-serif' }}>
                                Exemplo: Temperatura do Óleo &gt; 60°C
                            </div>
                            <Select<Opt, false, GroupBase<Opt>>
                                options={eventoOptions}
                                value={eventoSelected}
                                onChange={opt => setEventoCampo(opt?.value ?? '')}
                                placeholder="Buscar variável do evento..."
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={selectStyles}
                            />
                            <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 8 }}>
                                <select value={eventoCondicao} onChange={e => setEventoCondicao(e.target.value as any)} style={COND_STYLE}>
                                    <option value=">">&gt;</option>
                                    <option value="<">&lt;</option>
                                    <option value="=">=</option>
                                </select>
                                <input type="number" value={eventoValor} onChange={e => setEventoValor(e.target.value)} placeholder="Valor limite" style={{ ...INPUT_STYLE }} />
                            </div>
                        </div>
                    )}
                </div>

                <div style={DIVIDER} />

                {/* ── 3. Período da análise ── */}
                <style>{FP_STYLE}</style>
                <span style={SECTION_LABEL}>3 — Período da análise</span>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                    {/* Atalhos rápidos — usam applyShortcut para evitar flash */}
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const, marginBottom: 4 }}>
                        {[
                            { label: '1h',  ms: 1 * 60 * 60 * 1000 },
                            { label: '6h',  ms: 6 * 60 * 60 * 1000 },
                            { label: '24h', ms: 24 * 60 * 60 * 1000 },
                            { label: '7d',  ms: 7 * 24 * 60 * 60 * 1000 },
                        ].map(({ label, ms }) => (
                            <button key={label} onClick={() => applyShortcut(ms)}
                                style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontFamily: 'Nunito,sans-serif', border: 'none', cursor: 'pointer', background: 'rgba(82,146,209,0.15)', color: '#7BB8E8' }}>
                                {label}
                            </button>
                        ))}
                    </div>

                    <div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontFamily: 'Nunito,sans-serif', marginBottom: 3, letterSpacing: 0.3 }}>INÍCIO</div>
                        <div className="cor-fp-wrap">
                            {/* Sem value controlado — Flatpickr gerencia seu próprio display */}
                            <Flatpickr ref={fpInicioRef}
                                onChange={([d]) => d && setCorInicio(d)}
                                options={{ defaultDate: corInicio, enableTime: true, time_24hr: true, dateFormat: 'd/m/Y H:i', locale: Portuguese, appendTo: document.body, disableMobile: true }} />
                        </div>
                    </div>

                    <div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontFamily: 'Nunito,sans-serif', marginBottom: 3, letterSpacing: 0.3 }}>FIM</div>
                        <div className="cor-fp-wrap">
                            <Flatpickr ref={fpFimRef}
                                onChange={([d]) => d && setCorFim(d)}
                                options={{ defaultDate: corFim, enableTime: true, time_24hr: true, dateFormat: 'd/m/Y H:i', locale: Portuguese, appendTo: document.body, disableMobile: true }} />
                        </div>
                    </div>
                </div>

                {/* ── 4. Analisar ── */}
                {error && <div style={{ color: '#F03F3F', fontSize: 12, marginBottom: 8, fontFamily: 'Nunito,sans-serif' }}>{error}</div>}

                <button style={{ ...FILTER_BTN, padding: '12px 0', fontSize: 14, opacity: loading ? 0.7 : 1 }} onClick={run} disabled={loading}>
                    {loading ? 'Calculando...' : 'Analisar correlação'}
                </button>

                {result && !showResult && (
                    <button onClick={() => setShowResult(true)} style={{ width: '100%', marginTop: 8, padding: '8px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 12, fontFamily: 'Nunito,sans-serif' }}>
                        Ver resultado anterior
                    </button>
                )}
            </div>

            {/* Info modal */}
            {showInfo && (
                <Overlay title="O que é correlação?" onClose={() => setShowInfo(false)}>
                    <INFO_TEXT />
                </Overlay>
            )}

            {/* Result modal */}
            {showResult && result && (
                <Overlay
                    title={result.modo === 'evento' ? 'Correlação com evento' : 'Matriz de correlação'}
                    onClose={() => setShowResult(false)}
                >
                    <ResultContent result={result} categorias={categorias} />
                </Overlay>
            )}
        </>
    )
}

export default CorrelationPanel
